'use client';

import { createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { User } from 'firebase/auth';
import {
  applyActionCode,
  browserLocalPersistence,
  browserSessionPersistence,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
} from 'firebase/auth';

import { getFirebaseAuthClient, getGoogleProvider, hasFirebaseClientEnv } from '@/lib/firebase/client';
import { claimsCanAccessAdmin, mergeRolesWithClaims } from '@/lib/auth/claims';
import { canAccessStudio } from '@/lib/auth/roles';
import { fetchWithAppAuth } from '@/lib/auth/client-api';
import type { ProfileCompletionInput, UserDoc } from '@/types/platform';
import { toast } from '@/hooks/use-toast';
import { activateReaderDraftIdentity, clearReaderDraftsForSignOut, exportReaderDrafts, listReaderDrafts } from '@/lib/reader/drafts-client';
import { setReaderClientIdentity } from '@/lib/reader/client-identity';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';

export type AppSession = {
  uid: string;
  email: string;
  displayName?: string | null;
  roles: UserDoc['roles'];
  emailVerified: boolean;
  onboardingCompleted: boolean;
  authorProfileId?: string | null;
  plan: UserDoc['plan'];
  canAccessStudio: boolean;
  canAccessAdmin: boolean;
};

type AuthResult = { ok: true } | { ok: false; error: string };

type AppAuthContextValue = {
  firebaseReady: boolean;
  loading: boolean;
  user: User | null;
  profile: UserDoc | null;
  session: AppSession | null;
  signIn: (input: { email: string; password: string; remember?: boolean }) => Promise<AuthResult>;
  signUp: (input: { displayName: string; email: string; password: string; verificationNext?: string }) => Promise<AuthResult>;
  signInWithGoogle: (input?: { remember?: boolean }) => Promise<AuthResult>;
  signOutUser: () => Promise<AuthResult>;
  sendPasswordReset: (email: string) => Promise<AuthResult>;
  resendVerificationEmail: (next?: string) => Promise<AuthResult>;
  applyEmailVerificationCode: (code: string) => Promise<AuthResult>;
  completeProfileFlow: (input: ProfileCompletionInput) => Promise<AuthResult>;
  resetPasswordWithCode: (code: string, newPassword: string) => Promise<AuthResult>;
  refreshSession: () => Promise<void>;
  sessionSyncError: string | null;
};

export const AppAuthContext = createContext<AppAuthContextValue | null>(null);

function toErrorMessage(error: unknown) {
  const code =
    typeof error === 'object' && error && 'code' in error && typeof error.code === 'string'
      ? error.code
      : null;
  const message = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
  if (code && !message.includes(code)) return `${code}: ${message}`;
  if (message) return message;
  return 'Something went wrong. Please try again.';
}

function authStepError(step: string, error: unknown) {
  return `${step} failed: ${toErrorMessage(error)}`;
}

function isUnauthorizedContinueUri(error: unknown) {
  const code =
    typeof error === 'object' && error && 'code' in error && typeof error.code === 'string'
      ? error.code
      : '';
  const message = error instanceof Error ? `${code} ${error.message}` : String(error || '');
  return message.includes('auth/unauthorized-continue-uri');
}

function buildActionCodeSettings(path: string) {
  const configuredBase = (process.env.NEXT_PUBLIC_FIREBASE_AUTH_CONTINUE_URL || process.env.NEXT_PUBLIC_SITE_URL || '').trim();
  if (!configuredBase) return undefined;
  try {
    return { url: new URL(path, configuredBase).toString() };
  } catch {
    return undefined;
  }
}

async function sendVerificationEmailSafely(user: User, next?: string) {
  const safeNext = next === '/assessment/claim' || next?.startsWith('/claim?intent=') ? next : undefined;
  const settings = buildActionCodeSettings(`/verify-email${safeNext ? `?next=${encodeURIComponent(safeNext)}` : ''}`);
  try {
    if (settings) {
      await sendEmailVerification(user, settings);
      return;
    }
  } catch (error) {
    if (!isUnauthorizedContinueUri(error)) throw error;
  }
  await sendEmailVerification(user);
}

async function sendPasswordResetSafely(auth: ReturnType<typeof getFirebaseAuthClient>, email: string) {
  if (!auth) throw new Error('Firebase client environment is not configured yet.');
  const serverResponse = await fetch('/api/auth/password-reset/request', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
    credentials: 'same-origin',
  });
  const serverPayload = await serverResponse.json().catch(() => ({})) as { error?: string; fallbackToFirebase?: boolean };
  if (!serverResponse.ok) throw new Error(serverPayload.error || 'Unable to request a password reset.');
  if (!serverPayload.fallbackToFirebase) return;
  const settings = buildActionCodeSettings('/login');
  try {
    if (settings) {
      await sendPasswordResetEmail(auth, email, settings);
      return;
    }
  } catch (error) {
    if (!isUnauthorizedContinueUri(error)) throw error;
  }
  await sendPasswordResetEmail(auth, email);
}

function buildSession(user: User, profile: UserDoc | null, claims: Record<string, unknown> | null): AppSession {
  const roles = mergeRolesWithClaims(profile?.roles, claims);
  return {
    uid: user.uid,
    email: user.email ?? '',
    displayName: profile?.displayName ?? user.displayName,
    roles,
    emailVerified: user.emailVerified,
    onboardingCompleted: Boolean(profile?.onboardingCompleted),
    authorProfileId: profile?.authorProfileId ?? null,
    plan: profile?.plan ?? 'free',
    canAccessStudio: !['disabled', 'on_hold', 'suspended'].includes(profile?.accountStatus ?? 'active') && canAccessStudio(roles),
    canAccessAdmin: claimsCanAccessAdmin(claims),
  };
}

type ServerSessionSnapshot = {
  session: AppSession | null;
  profile: UserDoc | null;
};

async function fetchServerSessionSnapshot(): Promise<ServerSessionSnapshot> {
  const response = await fetch('/api/auth/session', {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
  });

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) return { session: null, profile: null };
    throw new Error('The server session could not be checked. Your local reader drafts are retained until your account can be verified.');
  }

  const payload = (await response.json().catch(() => null)) as ServerSessionSnapshot | null;
  if (!payload || !Object.prototype.hasOwnProperty.call(payload, 'session')) throw new Error('The server session response was incomplete. Local reader drafts have been retained.');
  return {
    session: payload?.session ?? null,
    profile: payload?.profile ?? null,
  };
}

function tokenExpiresSoon(expirationTime: string) {
  const expiresAt = Date.parse(expirationTime);
  if (!Number.isFinite(expiresAt)) return true;
  return expiresAt - Date.now() < 10 * 60 * 1000;
}

async function postSessionCookie(idToken: string) {
  const response = await fetch('/api/auth/session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken }),
    credentials: 'include',
  });

  if (!response.ok) {
    let message = 'Unable to establish the secure server session.';
    try {
      const payload = (await response.json()) as { error?: string };
      if (payload?.error) message = payload.error;
    } catch {}
    throw new Error(message);
  }

  const payload = (await response.json()) as { profile?: UserDoc };
  if (!payload.profile) {
    throw new Error('Server session endpoint did not return a user profile.');
  }
  return payload.profile;
}

let sessionCookieExchange: Promise<void> = Promise.resolve();
let sessionCookieGeneration = 0;

async function syncSignedInUser(user: User, options: { forceRefresh?: boolean; syncCookie?: boolean; isCurrent?: () => boolean } = {}) {
  const cookieGeneration = sessionCookieGeneration;
  const checkCurrent = () => {
    if (cookieGeneration !== sessionCookieGeneration || getFirebaseAuthClient()?.currentUser?.uid !== user.uid || options.isCurrent?.() === false) throw new Error('The active sign-in changed.');
  };
  checkCurrent();
  let tokenResult;
  try {
    const initialTokenResult = await user.getIdTokenResult(Boolean(options.forceRefresh));
    checkCurrent();
    tokenResult = !options.forceRefresh && tokenExpiresSoon(initialTokenResult.expirationTime)
      ? await user.getIdTokenResult(true)
      : initialTokenResult;
    checkCurrent();
  } catch (error) {
    throw new Error(authStepError('Firebase token/claims refresh', error));
  }

  if (options.syncCookie === false) {
    return { tokenResult, ensuredDoc: null };
  }

  try {
    // Serialize cookie exchanges: a slow response for account A must finish
    // before the verified exchange for account B, never overwrite it later.
    const exchange = sessionCookieExchange.catch(() => undefined).then(async () => {
      checkCurrent();
      const ensuredDoc = await postSessionCookie(tokenResult.token);
      checkCurrent();
      if (ensuredDoc.uid !== user.uid) throw new Error('The verified account profile does not match this sign-in.');
      return ensuredDoc;
    });
    sessionCookieExchange = exchange.then(() => undefined, () => undefined);
    const ensuredDoc = await exchange;
    return { tokenResult, ensuredDoc };
  } catch (error) {
    throw new Error(authStepError('Server session cookie exchange at /api/auth/session', error));
  }
}

function requireSyncedProfile(profile: UserDoc | null): UserDoc {
  if (!profile) {
    throw new Error('Server session endpoint did not return a user profile.');
  }
  return profile;
}

async function clearSessionCookie() {
  sessionCookieGeneration++;
  await sessionCookieExchange.catch(() => undefined);
  const response = await fetch('/api/auth/session', {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) throw new Error('The secure server session could not be signed out. Please reconnect and try again.');
}

export function AppAuthProvider({ children }: { children: ReactNode }) {
  const firebaseReady = hasFirebaseClientEnv();
  const [loading, setLoading] = useState(firebaseReady);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserDoc | null>(null);
  const [claims, setClaims] = useState<Record<string, unknown> | null>(null);
  const [serverSession, setServerSession] = useState<AppSession | null>(null);
  const [sessionSyncError, setSessionSyncError] = useState<string | null>(null);
  const lastSessionCookieUidRef = useRef<string | null>(null);
  const readerOwnerUidRef = useRef<string | null>(null);
  readerOwnerUidRef.current = user?.uid ?? serverSession?.uid ?? null;
  const signOutRequestedRef = useRef(false);
  const authOperationRef = useRef(0);
  const [readerSignOutDrafts, setReaderSignOutDrafts] = useState<{ uid: string; count: number; storageUnavailable: boolean } | null>(null);
  const [readerSignOutError, setReaderSignOutError] = useState<string | null>(null);
  const readerSignOutResolver = useRef<((proceed: boolean) => void) | null>(null);

  const activateReaderIdentity = useCallback((uid: string | null, verifiedProfile: UserDoc | null = null) => {
    // UID invalidation is synchronous; persistence cleanup follows before any
    // reader draft operation can write data for the previous account.
    const deletionPending = (verifiedProfile as (UserDoc & { privacyDeletionPendingAt?: unknown }) | null)?.privacyDeletionPendingAt != null;
    void activateReaderDraftIdentity(uid, { clearCurrent: deletionPending }).catch(() => undefined);
  }, []);

  const refreshSession = useCallback(async () => {
    const auth = getFirebaseAuthClient();
    const expectedUid = auth?.currentUser?.uid ?? null;
    const operation = ++authOperationRef.current;
    const current = () => operation === authOperationRef.current && (auth?.currentUser?.uid ?? null) === expectedUid && !signOutRequestedRef.current;
    try {
      if (!auth || !auth.currentUser) {
        const snapshot = await fetchServerSessionSnapshot();
        if (!current()) return;
        activateReaderIdentity(snapshot.session?.uid ?? null, snapshot.profile);
        setUser(null); setProfile(snapshot.profile); setClaims(null); setServerSession(snapshot.session); setSessionSyncError(null); setLoading(false);
        return;
      }
      await auth.currentUser.reload();
      if (!current() || !auth.currentUser) return;
      const refreshedUser = auth.currentUser;
      const { tokenResult, ensuredDoc } = await syncSignedInUser(refreshedUser, { forceRefresh: true, syncCookie: true, isCurrent: current });
      if (!current()) return;
      const syncedProfile = requireSyncedProfile(ensuredDoc);
      activateReaderIdentity(refreshedUser.uid, syncedProfile);
      setUser(refreshedUser); setProfile(syncedProfile); setClaims(tokenResult.claims as Record<string, unknown>); setServerSession(null);
      lastSessionCookieUidRef.current = refreshedUser.uid; setSessionSyncError(null); setLoading(false);
    } catch (error) { if (current()) throw error; }
  }, [activateReaderIdentity]);

  useEffect(() => {
    if (!firebaseReady) {
      return;
    }

    const auth = getFirebaseAuthClient();
    if (!auth) {
      const handle = window.setTimeout(() => setLoading(false), 0);
      return () => window.clearTimeout(handle);
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const operation = ++authOperationRef.current;
      const expectedUid = firebaseUser?.uid ?? null;
      const current = () => operation === authOperationRef.current && (auth.currentUser?.uid ?? null) === expectedUid;
      setLoading(true);
      setReaderClientIdentity(null);
      if (!firebaseUser) {
        if (signOutRequestedRef.current) {
          signOutRequestedRef.current = false;
          setUser(null);
          setProfile(null);
          setClaims(null);
          setServerSession(null);
          setSessionSyncError(null);
          setLoading(false);
          lastSessionCookieUidRef.current = null;
          return;
        }

        try {
          const snapshot = await fetchServerSessionSnapshot();
          if (!current()) return;
          activateReaderIdentity(snapshot.session?.uid ?? null, snapshot.profile);
          setUser(null); setProfile(snapshot.profile); setClaims(null); setServerSession(snapshot.session); setSessionSyncError(null);
        } catch (error) {
          if (!current()) return;
          // Offline/5xx is not evidence of logout. Hide authenticated content,
          // retain private local drafts, and recheck when connectivity returns.
          setUser(null); setProfile(null); setClaims(null); setServerSession(null); setSessionSyncError(toErrorMessage(error));
        }
        setLoading(false);
        lastSessionCookieUidRef.current = null;
        return;
      }
      try {
        const needsCookieSync = lastSessionCookieUidRef.current !== firebaseUser.uid;
        const { tokenResult, ensuredDoc } = await syncSignedInUser(firebaseUser, { syncCookie: needsCookieSync, isCurrent: () => current() && !signOutRequestedRef.current });
        if (!current() || signOutRequestedRef.current) return;
        if (!ensuredDoc) {
          const snapshot = await fetchServerSessionSnapshot();
          if (!current() || snapshot.session?.uid !== firebaseUser.uid) return;
          activateReaderIdentity(firebaseUser.uid, snapshot.profile);
        }
        setUser(firebaseUser);
        setServerSession(null);
        if (ensuredDoc) {
          activateReaderIdentity(firebaseUser.uid, ensuredDoc);
          setProfile(ensuredDoc);
          lastSessionCookieUidRef.current = firebaseUser.uid;
        }
        setClaims(tokenResult.claims as Record<string, unknown>);
        setSessionSyncError(null);
      } catch (error) {
        if (!current() || signOutRequestedRef.current) return;
        const message = toErrorMessage(error);
        try {
          const { tokenResult } = await syncSignedInUser(firebaseUser, { syncCookie: false, isCurrent: current });
          if (!current()) return;
          setUser(firebaseUser);
          setClaims(tokenResult.claims as Record<string, unknown>);
          setServerSession(null);
        } catch {
          if (!current()) return;
          setReaderClientIdentity(null);
          try {
            const snapshot = await fetchServerSessionSnapshot();
            if (!current()) return;
            activateReaderIdentity(snapshot.session?.uid ?? null, snapshot.profile);
            setUser(null); setProfile(snapshot.profile); setClaims(null); setServerSession(snapshot.session);
          } catch { if (current()) { setUser(null); setProfile(null); setClaims(null); setServerSession(null); } }
        }
        if (!current()) return;
        setSessionSyncError(message);
        toast({
          title: 'Authentication sync failed',
          description: message,
          variant: 'destructive',
        });
      } finally {
        if (current()) setLoading(false);
      }
    });

    const operations = authOperationRef;
    return () => { operations.current++; unsubscribe(); };
  }, [activateReaderIdentity, firebaseReady]);

  useEffect(() => {
    const reconnect = () => { void refreshSession().catch((error) => setSessionSyncError(toErrorMessage(error))); };
    window.addEventListener('online', reconnect);
    return () => window.removeEventListener('online', reconnect);
  }, [refreshSession]);

  const signIn = useCallback(async ({ email, password, remember = true }: { email: string; password: string; remember?: boolean }): Promise<AuthResult> => {
    const auth = getFirebaseAuthClient();
    if (!auth) return { ok: false, error: 'Firebase client environment is not configured yet.' };

    try {
      let credential;
      try {
        await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
        credential = await signInWithEmailAndPassword(auth, email, password);
      } catch (error) {
        throw new Error(authStepError('Firebase email/password sign-in', error));
      }

      const current = () => auth.currentUser?.uid === credential.user.uid && !signOutRequestedRef.current;
      const { ensuredDoc, tokenResult } = await syncSignedInUser(credential.user, { forceRefresh: true, syncCookie: true, isCurrent: current });
      if (!current()) return { ok: false, error: 'The active sign-in changed.' };
      const syncedProfile = requireSyncedProfile(ensuredDoc);
      setUser(credential.user);
      setProfile(syncedProfile);
      setClaims(tokenResult.claims as Record<string, unknown>);
      setServerSession(null);
      lastSessionCookieUidRef.current = credential.user.uid;
      return { ok: true };
    } catch (error) {
      return { ok: false, error: toErrorMessage(error) };
    }
  }, []);

  const signUp = useCallback(
    async ({ displayName, email, password, verificationNext }: { displayName: string; email: string; password: string; verificationNext?: string }): Promise<AuthResult> => {
      const auth = getFirebaseAuthClient();
      if (!auth) return { ok: false, error: 'Firebase client environment is not configured yet.' };

      try {
        const credential = await createUserWithEmailAndPassword(auth, email, password);
        const current = () => auth.currentUser?.uid === credential.user.uid && !signOutRequestedRef.current;
        if (!current()) return { ok: false, error: 'The active sign-in changed.' };
        if (displayName.trim()) {
          await updateProfile(credential.user, { displayName: displayName.trim() });
          if (!current()) return { ok: false, error: 'The active sign-in changed.' };
        }
        await sendVerificationEmailSafely(credential.user, verificationNext);
        if (!current()) return { ok: false, error: 'The active sign-in changed.' };
        const { ensuredDoc, tokenResult } = await syncSignedInUser(credential.user, { forceRefresh: true, syncCookie: true, isCurrent: current });
        if (!current()) return { ok: false, error: 'The active sign-in changed.' };
        const syncedProfile = requireSyncedProfile(ensuredDoc);
        setUser(credential.user);
        setProfile({ ...syncedProfile, displayName: displayName.trim() || syncedProfile.displayName });
        setClaims(tokenResult.claims as Record<string, unknown>);
        setServerSession(null);
        lastSessionCookieUidRef.current = credential.user.uid;
        return { ok: true };
      } catch (error) {
        return { ok: false, error: toErrorMessage(error) };
      }
    },
    [],
  );

  const signInWithGoogle = useCallback(async (input?: { remember?: boolean }): Promise<AuthResult> => {
    const auth = getFirebaseAuthClient();
    if (!auth) return { ok: false, error: 'Firebase client environment is not configured yet.' };

    try {
      let credential;
      try {
        await setPersistence(auth, input?.remember === false ? browserSessionPersistence : browserLocalPersistence);
        credential = await signInWithPopup(auth, getGoogleProvider());
      } catch (error) {
        throw new Error(authStepError('Firebase Google popup sign-in', error));
      }

      const current = () => auth.currentUser?.uid === credential.user.uid && !signOutRequestedRef.current;
      const { ensuredDoc, tokenResult } = await syncSignedInUser(credential.user, { forceRefresh: true, syncCookie: true, isCurrent: current });
      if (!current()) return { ok: false, error: 'The active sign-in changed.' };
      const syncedProfile = requireSyncedProfile(ensuredDoc);
      setUser(credential.user);
      setProfile(syncedProfile);
      setClaims(tokenResult.claims as Record<string, unknown>);
      setServerSession(null);
      lastSessionCookieUidRef.current = credential.user.uid;
      return { ok: true };
    } catch (error) {
      return { ok: false, error: toErrorMessage(error) };
    }
  }, []);

  const signOutUser = useCallback(async (): Promise<AuthResult> => {
    const auth = getFirebaseAuthClient();
    if (!auth) return { ok: false, error: 'Firebase client environment is not configured yet.' };
    if (loading) return { ok: false, error: 'Your account is still loading. Please wait before signing out so your local reader drafts can be checked.' };

    try {
      const uid = auth.currentUser?.uid ?? readerOwnerUidRef.current ?? lastSessionCookieUidRef.current;
      if (!uid && sessionSyncError) return { ok: false, error: 'Your account could not be verified. Reconnect and let your account load before signing out so your local reader drafts can be checked.' };
      const expectedFirebaseUid = auth.currentUser?.uid ?? null;
      const operation = authOperationRef.current;
      const current = () => operation === authOperationRef.current && (auth.currentUser?.uid ?? readerOwnerUidRef.current ?? lastSessionCookieUidRef.current) === uid;
      if (uid) {
        let draftCount = 0; let storageUnavailable = false;
        if (typeof indexedDB !== 'undefined') {
          try { draftCount = (await listReaderDrafts(uid)).length; }
          catch { storageUnavailable = true; }
        }
        if (!current()) return { ok: false, error: 'The active sign-in changed. Sign out from the current account when ready.' };
        if (draftCount || storageUnavailable) {
          if (readerSignOutResolver.current) return { ok: false, error: 'Finish the open sign-out dialog first.' };
          setReaderSignOutError(null);
          const proceed = await new Promise<boolean>((resolve) => { readerSignOutResolver.current = resolve; setReaderSignOutDrafts({ uid, count: draftCount, storageUnavailable }); });
          if (!current()) return { ok: false, error: 'The active sign-in changed. Your current account remains signed in.' };
          if (!proceed) return { ok: false, error: 'Sign-out cancelled. Your local reader drafts are still on this device.' };
        }
        signOutRequestedRef.current = true;
        authOperationRef.current++;
        try { await clearReaderDraftsForSignOut(uid); }
        catch { toast({ title: 'Local reader cleanup could not be verified', description: 'Sign-out will continue. Copy any note still visible before closing its page. Local drafts will be cleared on the next successful account check when browser storage is available.', variant: 'destructive' }); }
      } else { signOutRequestedRef.current = true; authOperationRef.current++; setReaderClientIdentity(null); }
      if ((auth.currentUser?.uid ?? null) !== expectedFirebaseUid) {
        signOutRequestedRef.current = false; await refreshSession();
        return { ok: false, error: 'The active sign-in changed. Your current account remains signed in.' };
      }
      signOutRequestedRef.current = true;
      await clearSessionCookie();
      if ((auth.currentUser?.uid ?? null) !== expectedFirebaseUid) {
        signOutRequestedRef.current = false; await refreshSession();
        return { ok: false, error: 'The active sign-in changed. Your current account remains signed in.' };
      }
      await signOut(auth);
      setUser(null);
      setProfile(null);
      setClaims(null);
      setServerSession(null);
      lastSessionCookieUidRef.current = null;
      return { ok: true };
    } catch (error) {
      signOutRequestedRef.current = false;
      setReaderClientIdentity(null);
      await refreshSession().catch(() => undefined);
      return { ok: false, error: toErrorMessage(error) };
    }
  }, [loading, refreshSession, sessionSyncError]);

  const sendPasswordReset = useCallback(async (email: string): Promise<AuthResult> => {
    const auth = getFirebaseAuthClient();
    if (!auth) return { ok: false, error: 'Firebase client environment is not configured yet.' };

    try {
      await sendPasswordResetSafely(auth, email);
      return { ok: true };
    } catch (error) {
      return { ok: false, error: toErrorMessage(error) };
    }
  }, []);

  const resendVerificationEmail = useCallback(async (next?: string): Promise<AuthResult> => {
    const auth = getFirebaseAuthClient();
    if (!auth?.currentUser) return { ok: false, error: 'You need to sign in before requesting another verification email.' };

    try {
      await sendVerificationEmailSafely(auth.currentUser, next);
      return { ok: true };
    } catch (error) {
      return { ok: false, error: toErrorMessage(error) };
    }
  }, []);

  const applyEmailVerificationCode = useCallback(async (code: string): Promise<AuthResult> => {
    const auth = getFirebaseAuthClient();
    if (!auth) return { ok: false, error: 'Firebase client environment is not configured yet.' };

    try {
      await applyActionCode(auth, code);
      await refreshSession();
      return { ok: true };
    } catch (error) {
      return { ok: false, error: toErrorMessage(error) };
    }
  }, [refreshSession]);

  const completeProfileFlow = useCallback(
    async (input: ProfileCompletionInput): Promise<AuthResult> => {
      const auth = getFirebaseAuthClient();
      if (!auth?.currentUser) return { ok: false, error: 'You need to be signed in before completing your profile.' };

      try {
        if (input.displayName.trim() !== (auth.currentUser.displayName ?? '').trim()) {
          await updateProfile(auth.currentUser, { displayName: input.displayName.trim() });
        }
        const response = await fetchWithAppAuth('/api/account/profile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'complete-profile', input }),
        });
        const payload = (await response.json().catch(() => ({}))) as { error?: string; profile?: UserDoc };
        if (!response.ok || !payload.profile) {
          throw new Error(payload.error || 'Unable to complete profile.');
        }
        await refreshSession();
        setProfile(payload.profile);
        return { ok: true };
      } catch (error) {
        return { ok: false, error: toErrorMessage(error) };
      }
    },
    [refreshSession],
  );

  const resetPasswordWithCode = useCallback(async (code: string, newPassword: string): Promise<AuthResult> => {
    const auth = getFirebaseAuthClient();
    if (!auth) return { ok: false, error: 'Firebase client environment is not configured yet.' };

    try {
      await confirmPasswordReset(auth, code, newPassword);
      return { ok: true };
    } catch (error) {
      return { ok: false, error: toErrorMessage(error) };
    }
  }, []);

  const value = useMemo<AppAuthContextValue>(() => {
    const session = user ? buildSession(user, profile, claims) : serverSession;
    return {
      firebaseReady,
      loading,
      user,
      profile,
      session,
      signIn,
      signUp,
      signInWithGoogle,
      signOutUser,
      sendPasswordReset,
      resendVerificationEmail,
      applyEmailVerificationCode,
      completeProfileFlow,
      resetPasswordWithCode,
      refreshSession,
      sessionSyncError,
    };
  }, [firebaseReady, loading, user, profile, claims, serverSession, signIn, signUp, signInWithGoogle, signOutUser, sendPasswordReset, resendVerificationEmail, applyEmailVerificationCode, completeProfileFlow, resetPasswordWithCode, refreshSession, sessionSyncError]);

  function finishReaderSignOut(proceed: boolean) {
    readerSignOutResolver.current?.(proceed);
    readerSignOutResolver.current = null;
    setReaderSignOutDrafts(null);
  }

  return <AppAuthContext.Provider value={value}>{children}
    <Dialog open={Boolean(readerSignOutDrafts)} onOpenChange={(open) => { if (!open) finishReaderSignOut(false); }}><DialogContent>
      <DialogTitle>{readerSignOutDrafts?.storageUnavailable ? 'Local reader storage is unavailable' : 'Save your local reader drafts before signing out'}</DialogTitle>
      <DialogDescription>{readerSignOutDrafts?.storageUnavailable ? 'We cannot read or export local reader drafts right now. Copy any unfinished note that is still visible before signing out. You can stay signed in, or sign out anyway; local cleanup will be retried when browser storage becomes available.' : `You have ${readerSignOutDrafts?.count ?? 0} reader changes that are not synchronized. Signing out clears local drafts from this device. You can stay signed in to finish them, or download a private copy before signing out.`}</DialogDescription>
      {readerSignOutError && <p role="alert" className="text-sm text-red-800">{readerSignOutError}</p>}
      <div className="flex flex-wrap gap-2"><Button variant="outline" onClick={() => finishReaderSignOut(false)}>Stay signed in</Button><Button className="bg-[#365b44] text-white hover:bg-[#294535]" onClick={() => {
        if (!readerSignOutDrafts) return;
        if (readerSignOutDrafts.storageUnavailable) { finishReaderSignOut(true); return; }
        void exportReaderDrafts(readerSignOutDrafts.uid).then(() => finishReaderSignOut(true)).catch((error) => setReaderSignOutError(toErrorMessage(error)));
      }}>{readerSignOutDrafts?.storageUnavailable ? 'Sign out anyway' : 'Export drafts and sign out'}</Button></div>
    </DialogContent></Dialog>
  </AppAuthContext.Provider>;
}
